import { useCallback, useRef, useSyncExternalStore } from "react";

/**
 * A minimal external store: one immutable state object, replaced as a whole, and listeners told when it changed.
 * Components read it through `useStoreSelector`, so one that selects a small part of the state is re-rendered only
 * when that part changes, however often the rest does.
 */
export function createStore(initialState) {
  let state = initialState;
  const listeners = new Set();

  return {
    getState: () => state,

    /** Replaces the state with the result of `update(state)`, or merges an object into it. Nothing changes if the result is the same object. */
    setState(update) {
      const next = typeof update === "function" ? update(state) : { ...state, ...update };
      if (next === state) return;

      state = next;
      for (const listener of [...listeners]) listener();
    },

    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

/**
 * Reads a part of a store's state. The component renders again only when the selected value changes, as decided
 * by `isEqual` (reference equality by default). A selector that builds a new object every time needs a custom
 * `isEqual`, or it will be asked for a stable result by returning a part of the state instead.
 */
export function useStoreSelector(store, selector, isEqual = Object.is) {
  const cache = useRef({ state: undefined, selector: undefined, value: undefined, hasValue: false });

  const getSnapshot = useCallback(() => {
    const state = store.getState();
    const cached = cache.current;

    if (cached.hasValue && cached.state === state && cached.selector === selector) return cached.value;

    const value = selector(state);
    if (cached.hasValue && isEqual(cached.value, value)) {
      cached.state = state;
      cached.selector = selector;
      return cached.value;
    }

    cache.current = { state, selector, value, hasValue: true };
    return value;
  }, [store, selector, isEqual]);

  return useSyncExternalStore(store.subscribe, getSnapshot, getSnapshot);
}
